import type { TPlayer } from '@setplex/pureya'
import type { PlaybackSnapshot } from './models/play-start-stop'
import type { Action, PlayerState, Status } from './player-state'

import { Intent } from '@setplex/pureya'
import { Intent as AppIntent, PlayerIntentMessage } from '~/entities/player'
import { mq } from '~/shared/mq'
import { logger, PlayStartReason, PlayStopReason } from './index.h'
import { playStart, playStop } from './models/play-start-stop'

// ─── Intent → reason mappings ────────────────────────────────────────────────

const INTENT_TO_START_REASON = {
  [AppIntent.AutoPreview]: PlayStartReason.autoPreview,
  [AppIntent.TrailerVisibleByScroll]: PlayStartReason.autoPreview,
  [AppIntent.OpenPlayer]: PlayStartReason.userPlay,
  [AppIntent.SelectEpisodeFromPlaylist]: PlayStartReason.userClick,
  [AppIntent.SelectChannelFromEpg]: PlayStartReason.userClick,
  [Intent.TogglePause]: PlayStartReason.userPlay,
  [Intent.PlayNext]: PlayStartReason.userClick,
  [Intent.PlayNextEpisode]: PlayStartReason.userClick,
  [Intent.PlayPrevious]: PlayStartReason.userClick,
  [Intent.GoLive]: PlayStartReason.userClick,
  [Intent.ToggleCast]: PlayStartReason.userCast,
  [Intent.ChangeQuality]: PlayStartReason.changeBitrate,
  [Intent.ChangeAudio]: PlayStartReason.changeAudio,
  [Intent.Rewind]: PlayStartReason.userMove,
  [Intent.Seek]: PlayStartReason.userMove,
  // [Intent.AutoPreview]: PlayStartReason.autoPreview, // intent not yet defined
  // [Intent.AppUp]: PlayStartReason.appUp, // intent not yet defined
} as const

const INTENT_TO_STOP_REASON = {
  [AppIntent.TrailerHiddenByScroll]: PlayStopReason.userScroll,
  [AppIntent.UserExit]: PlayStopReason.userExit,
  [AppIntent.OtherContent]: PlayStopReason.otherContent,
  [AppIntent.SelectEpisodeFromPlaylist]: PlayStopReason.userClick,
  [AppIntent.SelectChannelFromEpg]: PlayStopReason.userClick,
  [Intent.TogglePause]: PlayStopReason.userPause,
  [Intent.Rewind]: PlayStopReason.userMove,
  [Intent.Seek]: PlayStopReason.userMove,
  [Intent.ClosePlayer]: PlayStopReason.userBack,
  [Intent.PlayNext]: PlayStopReason.userClick,
  [Intent.PlayNextEpisode]: PlayStopReason.userClick,
  [Intent.PlayPrevious]: PlayStopReason.userClick,
  [Intent.GoLive]: PlayStopReason.userClick,
  [Intent.ToggleCast]: PlayStopReason.userCast,
  [Intent.ChangeQuality]: PlayStopReason.changeBitrate,
  [Intent.ChangeAudio]: PlayStopReason.changeAudio,
  // [Intent.UserScroll]: PlayStopReason.userScroll, // intent not yet defined
  // [Intent.UserExit]: PlayStopReason.userExit, // intent not yet defined
  // [Intent.OtherContent]: PlayStopReason.otherContent, // intent not yet defined
  // [Intent.AppHide]: PlayStopReason.appHide, // intent not yet defined
} as const

// ─── Logical state machine ────────────────────────────────────────────────────
//
// Sits on top of the raw player state machine and emits only meaningful
// phase transitions to the watcher, absorbing startup/seek/buffering noise.
//
// Transitions:
//   Inactive → Loading    on: real → Loading
//   Loading  → Playing    on: real → Playing, debounced
//   Loading  → Inactive   on: real → Paused, Error, Disposed
//   Playing  → Stopped    on: real → Paused, Ended, Error, Disposed, Buffering (debounced)
//   Stopped  → Playing    on: real → Playing, debounced
//   Stopped  → Loading    on: real → Loading

type LogicalStateName = 'Inactive' | 'Loading' | 'Playing' | 'Stopped'

// How long real → Playing must be stable before logical → Playing is confirmed.
// Absorbs initial Loading→Buffering→Seeking→Playing noise and restore seeks.
export const PLAYING_DEBOUNCE_MS = 1_000

// How long real → Buffering must persist before logical Playing → Stopped fires.
// Absorbs brief mid-playback stalls that resolve on their own.
export const BUFFERING_DEBOUNCE_MS = 1_000

// How long the player may stay in Loading/Buffering before the user is told
// "playback started" anyway, immediately followed by stop(streamBuffering).
// Resets each time the real player re-enters Loading (new source).
export const STARTUP_TIMEOUT_MS = 5_000

/**
 * Compact logical state machine that suppresses raw player-noise and emits only
 * analytics-significant transitions.
 */
class LogicalStateMachine {
  private state: LogicalStateName = 'Inactive'
  private timer: ReturnType<typeof setTimeout> | null = null
  private startupTimer: ReturnType<typeof setTimeout> | null = null
  // Set when the startup timeout fires. Causes the next Loading→Playing
  // debounce to call onPlaying(fromLoading=false) so recovery defaults to
  // bufferFull rather than userPlay. Cleared when a new source enters Loading.
  private startupTimeoutFired = false

  private readonly onPlaying: (fromLoading: boolean) => void
  private readonly onStopped: (
    kind: 'streamBuffering' | 'streamError' | 'streamEnd'
  ) => void
  private readonly onStartupTimeout: () => void

  constructor(
    onPlaying: (fromLoading: boolean) => void,
    onStopped: (kind: 'streamBuffering' | 'streamError' | 'streamEnd') => void,
    onStartupTimeout: () => void
  ) {
    this.onPlaying = onPlaying
    this.onStopped = onStopped
    this.onStartupTimeout = onStartupTimeout
  }

  get isLoading(): boolean {
    return this.state === 'Loading'
  }

  get isPlaying(): boolean {
    return this.state === 'Playing'
  }

  // Called directly for user-initiated seeks — emits stop immediately then
  // waits for real → Playing to stabilise before emitting start.
  // Returns true if the machine was in Playing and acted, false otherwise.
  seek(): boolean {
    if (this.state !== 'Playing') return false
    this.cancelTimer()
    this.onStopped('streamBuffering')
    this.state = 'Stopped'
    return true
  }

  feed(
    to: Status['type'],
    stopKind:
      | 'streamBuffering'
      | 'streamError'
      | 'streamEnd' = 'streamBuffering'
  ) {
    this.cancelTimer()

    switch (this.state) {
      case 'Inactive':
        if (to === 'Loading') {
          this.enter('Loading')
        } else if (to === 'Playing') {
          // Some trailer flows resume directly from paused startup state
          // (Loading→Paused while offscreen, then Paused→Playing on scroll).
          // Treat this as a regular resumed start.
          this.timer = setTimeout(
            () => this.enter('Playing'),
            PLAYING_DEBOUNCE_MS
          )
        }
        break

      case 'Loading':
        if (to === 'Playing') {
          // Debounce: wait for Playing to be stable before confirming.
          // Cancel the startup timeout — playback is resolving in time.
          this.cancelStartupTimer()
          this.timer = setTimeout(
            () => this.enter('Playing'),
            PLAYING_DEBOUNCE_MS
          )
        } else if (to === 'Paused' || to === 'Error' || to === 'Disposed') {
          this.cancelStartupTimer()
          this.enter('Inactive')
        } else if (to === 'Loading') {
          // New source while already loading — reset the startup timer and clear
          // the timeout flag to give the new source its full fresh budget
          this.startupTimeoutFired = false
          this.resetStartupTimer()
        }
        // Buffering/Seeking during Loading is startup noise — stay in Loading,
        // startup timeout keeps ticking (or was just reset above)
        break

      case 'Playing':
        if (to === 'Buffering' || to === 'Seeking') {
          // Involuntary stall or non-user seek: debounce before stopping
          this.timer = setTimeout(
            () => this.enter('Stopped'),
            BUFFERING_DEBOUNCE_MS
          )
        } else if (
          to === 'Paused' ||
          to === 'Ended' ||
          to === 'Error' ||
          to === 'Disposed' ||
          to === 'Idle' // player reset (e.g. content switch)
        ) {
          this.enter('Stopped', stopKind)
        }
        // Playing → Playing is a no-op
        break

      case 'Stopped':
        if (to === 'Playing') {
          // Debounce: absorbs re-buffer on resume and post-seek buffering
          this.timer = setTimeout(
            () => this.enter('Playing'),
            PLAYING_DEBOUNCE_MS
          )
        } else if (to === 'Loading') {
          this.enter('Loading')
        } else if (to === 'Error' || to === 'Disposed' || to === 'Idle') {
          // no-op — already stopped
        }
        break
    }
  }

  private enter(
    next: LogicalStateName,
    stopKind:
      | 'streamBuffering'
      | 'streamError'
      | 'streamEnd' = 'streamBuffering'
  ) {
    const prev = this.state
    this.state = next
    if (next === 'Loading') {
      // (Re-)arm the startup timeout — gives the player STARTUP_TIMEOUT_MS to
      // reach stable Playing before we emit start+stop to signal the stall.
      this.startupTimeoutFired = false
      this.resetStartupTimer()
    } else if (next === 'Playing') {
      // fromLoading=true only when coming from Loading AND the startup timeout
      // has not yet fired. After a timeout the initial-load intent is already
      // consumed, so recovery must default to bufferFull (fromLoading=false).
      const fromLoading = prev === 'Loading' && !this.startupTimeoutFired
      this.onPlaying(fromLoading)
    } else if (next === 'Stopped') {
      this.onStopped(stopKind)
    }
  }

  private resetStartupTimer() {
    this.cancelStartupTimer()
    this.startupTimer = setTimeout(() => {
      this.startupTimer = null
      this.startupTimeoutFired = true
      this.onStartupTimeout()
    }, STARTUP_TIMEOUT_MS)
  }

  private cancelTimer() {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private cancelStartupTimer() {
    if (this.startupTimer !== null) {
      clearTimeout(this.startupTimer)
      this.startupTimer = null
    }
  }

  dispose() {
    this.cancelTimer()
    this.cancelStartupTimer()
  }
}

// ─── PlayerWatcher ────────────────────────────────────────────────────────────

export class PlayerWatcher {
  private unsubscribe: () => void
  private intentQueue: (Intent | AppIntent)[] = []
  private hadStart = false
  private autoplayAfterEndPending = false
  private logical: LogicalStateMachine
  private pipPauseTimer: ReturnType<typeof setTimeout> | null = null
  private awaitPipPauseAfterToggle = false
  private forcedNextStopReason: PlayStopReason | null = null
  // When Intent.StartAd arrives before play_start (pre-roll), defer play_stop until after play_start.
  private pendingAdStart = false
  // Skip Intro / Skip Credits: play_stop immediately, play_start after seek resumes.
  private pendingSkipStartReason: PlayStartReason | null = null
  // Set when Playing→Idle (playerreset) fires while logical is Playing.
  // Holds the pending stop timer so that if Error arrives synchronously in the
  // same tick, we can cancel the streamBuffering stop, emit streamError instead,
  // and skip the spurious pre-start emitStartForPreStartError.
  private playerResetTimer: ReturnType<typeof setTimeout> | null = null

  private static readonly PIP_PAUSE_DEBOUNCE_MS = 150

  private isPictureInPictureActive(): boolean {
    try {
      return Boolean(this.player.isInPictureInPicture?.())
    } catch {
      return false
    }
  }

  private isChromecastConnected(): boolean {
    try {
      const manager = this.player.chromecastSessionManager
      const isConnected = manager?.constructor?.isChromecastConnected
      return typeof isConnected === 'function' ? Boolean(isConnected()) : false
    } catch {
      return false
    }
  }

  public readonly state: PlayerState
  public readonly player: TPlayer
  public readonly playerId: string

  constructor(
    state: PlayerState,
    player: TPlayer = state.player,
    playerId: string = state.playerId
  ) {
    this.state = state
    this.player = player
    this.playerId = playerId
    this.logical = new LogicalStateMachine(
      (fromLoading) => this.onLogicalPlaying(fromLoading),
      (kind) => this.onLogicalStopped(kind),
      () => this.onLogicalStartupTimeout()
    )
    this.unsubscribe = mq.on(PlayerIntentMessage, this.appIntent)
  }

  /**
   * App-wide intents (outside video.js control surface), scoped to this player.
   */
  appIntent = ({ player, intent }: PlayerIntentMessage) => {
    const isForMe = player ? player.id_ === this.playerId : this.state.visible
    if (!isForMe) return

    this.trace(`app intent ${intent}`)
    this.queueIntent(intent)
  }

  /**
   * Player-local intent feed.
   */
  intent(intent: Intent, at = Date.now()) {
    this.trace(`intent ${intent} at ${at}`)
    // PiP analytics are emitted from explicit enter/leave PiP actions.
    // Ignore raw toggle intent to avoid duplicate stop/start pairs.
    if (intent === Intent.TogglePip) return
    // Ad start: emit play_stop after play_start. If play_start not yet sent (pre-roll), defer.
    // When rewinding into ad: SmartLib fires onAdBreakBegin before our debounced onLogicalPlaying.
    // Defer play_stop(advertisingStart) until after play_start(userMove) so the order is correct.
    if (intent === Intent.StartAd) {
      const seekIntentPending = this.peekIntent([Intent.Rewind, Intent.Seek])
      if (this.hadStart && !seekIntentPending) {
        this.emitStop(PlayStopReason.advertisingStart)
      } else {
        this.pendingAdStart = true
      }
      return
    }
    if (intent === Intent.EndAd) {
      this.emitStart(PlayStartReason.advertisingStop)
      return
    }
    this.queueIntent(intent)
  }

  /**
   * Skip Intro / Skip Credits — manual segment skip (not Intent.Seek).
   */
  skipSegment(startReason: PlayStartReason, stopReason: PlayStopReason) {
    if (this.state.status?.type === 'Paused') {
      this.emitStop(stopReason)
      this.emitStart(startReason)
      return
    }

    this.pendingSkipStartReason = startReason
    this.forcedNextStopReason = stopReason

    if (this.logical.isPlaying) {
      this.logical.seek()
    }
  }

  /**
   * Player action feed (track/quality changes etc).
   */
  action(action: Action, at = Date.now()) {
    this.trace(`action ${action.type} at ${at}`)

    // ChangeAudio and ChangeQuality emit stop+start synchronously while staying
    // in logical Playing — but only when triggered by the user (intent present).
    // Automatic track selection (e.g. initial audio track) has no preceding intent
    // and should be silently ignored.
    if (action.type === 'ChangeAudio') {
      if (this.consumeIntent([Intent.ChangeAudio])) {
        const nextAudioTrackLanguage = action.payload || ''
        this.emitStop(PlayStopReason.changeAudio)
        this.state.audioTrackLanguage = nextAudioTrackLanguage
        this.emitStart(PlayStartReason.changeAudio)
      }
    } else if (action.type === 'ChangeQuality') {
      if (this.consumeIntent([Intent.ChangeQuality])) {
        this.emitStop(PlayStopReason.changeBitrate)
        if (action.payload != null) {
          this.state.videoQuality = String(action.payload)
        }
        this.emitStart(PlayStartReason.changeBitrate)
      }
    } else if (action.type === 'ToggleCast') {
      this.emitStop(PlayStopReason.userCast)
      // player self restarts with new tech on remoute device.
      this.emitStart(PlayStartReason.userCast)
    } else if (action.type === 'TogglePip') {
      const inPip = this.isPictureInPictureActive()

      // PiP toggle while paused (both enter and leave): emit a balanced
      // start+stop cycle that preserves paused final state and keeps play gate
      // consistent for subsequent PiP toggles.
      if (this.state.status?.type === 'Paused') {
        this.awaitPipPauseAfterToggle = false
        this.forcedNextStopReason = null
        this.emitStart(PlayStartReason.userPip)
        this.emitStop(PlayStopReason.userPause)
        return
      }

      // Enter PiP: always emit cycle.
      if (inPip) {
        this.awaitPipPauseAfterToggle = false
        this.forcedNextStopReason = null
        this.emitStop(PlayStopReason.userPip)
        this.emitStart(PlayStartReason.userPip)
        return
      }

      // Leave PiP while playing: emit cycle and arm short pause debounce for
      // browsers that fire pause right after PiP close.
      this.emitStop(PlayStopReason.userPip)
      this.emitStart(PlayStartReason.userPip)
      this.awaitPipPauseAfterToggle = true
    }
  }

  /**
   * Raw state transition feed from PlayerState.
   */
  transition(_from: Status, to: Status, _duration: number, at = Date.now()) {
    this.trace(`transition to ${to.type} at ${at}`)

    if (to.type === 'Playing' && this.pipPauseTimer) {
      clearTimeout(this.pipPauseTimer)
      this.pipPauseTimer = null
      this.awaitPipPauseAfterToggle = false
      this.forcedNextStopReason = null
    }

    if (to.type === 'Loading' || to.type === 'Idle') {
      if (to.type === 'Idle' || this.hadStart) {
        this.pendingAdStart = false
      }
      this.hadStart = false
      this.clearPendingSkipSegment()
    }

    if (to.type === 'Error' || to.type === 'Disposed') {
      this.clearPendingSkipSegment()
    }

    // Playing→Idle is a playerreset — the player may immediately follow with
    // an Error transition in the same JS turn. If we feed streamBuffering now
    // and Error arrives next, we get stop(streamBuffering) + phantom start(…).
    // Defer only when no intent that explains the Idle is queued (i.e. no
    // content-switch or seek intent). Those produce intentional resets whose
    // stop must fire immediately. An unintentional playerreset (e.g. a network
    // error) has no such intent, so we defer to let the Error coalesce.
    if (
      to.type === 'Idle' &&
      this.logical.isPlaying &&
      !this.peekIntent(PlayerWatcher.PEEK_ON_STOP_INTENTS)
    ) {
      if (this.playerResetTimer !== null) clearTimeout(this.playerResetTimer)
      this.playerResetTimer = setTimeout(() => {
        this.playerResetTimer = null
        this.logical.feed('Idle', 'streamBuffering')
      }, 0)
      return
    }

    // If Error arrives while the deferred playerreset stop is still pending,
    // cancel the deferred stop, feed streamError directly (which emits a single
    // stop(streamError)), and skip the pre-start error path entirely.
    if (to.type === 'Error' && this.playerResetTimer !== null) {
      clearTimeout(this.playerResetTimer)
      this.playerResetTimer = null
      this.logical.feed('Error', 'streamError')
      return
    }

    if (to.type === 'Error' && !this.hadStart) {
      this.emitStartForPreStartError()
    }

    // PiP close can cause a browser-generated pause right after we emitted
    // stop/start(userPip). Defer this pause; if playback resumes quickly, drop it.
    // If it stays paused, emit one more stop(userPip) to keep gate state correct.
    if (to.type === 'Paused' && this.awaitPipPauseAfterToggle) {
      this.awaitPipPauseAfterToggle = false
      this.forcedNextStopReason = PlayStopReason.userPip
      if (this.pipPauseTimer) clearTimeout(this.pipPauseTimer)
      this.pipPauseTimer = setTimeout(() => {
        this.pipPauseTimer = null
        this.logical.feed('Paused')
      }, PlayerWatcher.PIP_PAUSE_DEBOUNCE_MS)
      return
    }

    // User-initiated seek: emit stop immediately via seek(), then the normal
    // Stopped→Playing debounce will fire start when playback actually resumes.
    if (
      to.type === 'Seeking' &&
      (this.peekIntent([Intent.Rewind, Intent.Seek]) ||
        this.pendingSkipStartReason)
    ) {
      if (this.logical.seek()) {
        // Was Playing — stop emitted, start will fire on resume
      } else if (!this.logical.isLoading && !this.pendingSkipStartReason) {
        // Was Paused/Stopped/Inactive — already stopped. Discard the intent so
        // it doesn't bleed into the next stop or start event.
        // Do NOT discard when logical is Loading: a live-rewind causes
        // Playing→Idle (stop already emitted via feed) then Loading, and a
        // Seeking transition may arrive while logical is still in Loading.
        // The intent must survive so onLogicalPlaying can emit start(userMove).
        this.consumeIntent([Intent.Rewind, Intent.Seek])
      }
      return
    }

    const stopKind =
      to.type === 'Error'
        ? 'streamError'
        : to.type === 'Ended'
          ? 'streamEnd'
          : 'streamBuffering'
    this.logical.feed(to.type, stopKind)
  }

  /**
   * Disposes timers + mq subscription.
   */
  off() {
    if (this.pipPauseTimer) {
      clearTimeout(this.pipPauseTimer)
      this.pipPauseTimer = null
    }
    if (this.playerResetTimer) {
      clearTimeout(this.playerResetTimer)
      this.playerResetTimer = null
    }
    this.clearPendingSkipSegment()
    this.logical.dispose()
    this.unsubscribe()
  }

  // ─── Logical state handlers ───────────────────────────────────────────────

  // Intents that drive both a stop and a subsequent start.
  // onLogicalStopped peeks (does not consume) so the intent remains
  // available for onLogicalPlaying to consume when playback resumes.
  private static readonly PEEK_ON_STOP_INTENTS: readonly (
    | Intent
    | AppIntent
  )[] = [
    // Content switches — player resets and loads new content
    AppIntent.SelectEpisodeFromPlaylist,
    AppIntent.SelectChannelFromEpg,
    Intent.PlayNext,
    Intent.PlayNextEpisode,
    Intent.PlayPrevious,
    Intent.GoLive,
    // Seeks — player stays in same content, resumes after buffering
    Intent.Rewind,
    Intent.Seek,
  ]

  private onLogicalPlaying(fromLoading: boolean) {
    if (this.pendingSkipStartReason) {
      const reason = this.pendingSkipStartReason
      this.pendingSkipStartReason = null
      this.emitStart(reason)
      return
    }

    const intent = this.consumeIntent(
      Object.keys(INTENT_TO_START_REASON) as (Intent | AppIntent)[]
    )
    // Default differs by path:
    //   Loading → Playing  (initial load / content switch): userPlay
    //   Stopped → Playing  (resume after pause or stall):   bufferFull
    const defaultReason: PlayStartReason = fromLoading
      ? 'userPlay'
      : 'bufferFull'
    const reason: PlayStartReason = intent
      ? INTENT_TO_START_REASON[intent]
      : fromLoading && this.autoplayAfterEndPending
        ? PlayStartReason.autoPlay
        : defaultReason
    this.autoplayAfterEndPending = false

    if (!intent) this.trace(`playing with no pending intent`)
    this.emitStart(reason)
  }

  private onLogicalStartupTimeout() {
    // User has been waiting too long (> STARTUP_TIMEOUT_MS) for playback to start.
    // Emit start with whatever intent is queued (same reason a fast load would use),
    // then immediately stop(streamBuffering) so analytics reflects the stall.
    // The machine stays in Loading — when Playing eventually stabilises,
    // onLogicalPlaying fires again with bufferFull (intent already consumed here).
    const intent = this.consumeIntent(
      Object.keys(INTENT_TO_START_REASON) as (Intent | AppIntent)[]
    )
    const reason: PlayStartReason = intent
      ? INTENT_TO_START_REASON[intent]
      : 'bufferFull'
    this.emitStart(reason)
    this.emitStop(PlayStopReason.streamBuffering)
  }

  private onLogicalStopped(
    kind: 'streamBuffering' | 'streamError' | 'streamEnd'
  ) {
    if (kind === 'streamError') {
      this.autoplayAfterEndPending = false
      this.emitStop(PlayStopReason.streamError)
      return
    }
    if (kind === 'streamEnd') {
      this.autoplayAfterEndPending = true
      this.emitStop(PlayStopReason.streamEnd)
      return
    }
    this.autoplayAfterEndPending = false

    if (this.forcedNextStopReason) {
      const reason = this.forcedNextStopReason
      this.forcedNextStopReason = null
      this.emitStop(reason)
      return
    }
    if (this.isChromecastConnected()) {
      return
    }

    // For intents that drive both stop and start (seeks, content switches),
    // only peek — the intent must remain in the queue for onLogicalPlaying.
    const peekIntent = this.peekIntent(PlayerWatcher.PEEK_ON_STOP_INTENTS)
    if (peekIntent) {
      const reason =
        INTENT_TO_STOP_REASON[
          peekIntent as keyof typeof INTENT_TO_STOP_REASON
        ] ?? 'streamBuffering'
      this.emitStop(reason)
      return
    }

    // All other intents: consume normally
    const intent = this.consumeIntent(
      Object.keys(INTENT_TO_STOP_REASON) as (Intent | AppIntent)[]
    )
    const reason: PlayStopReason = intent
      ? INTENT_TO_STOP_REASON[intent]
      : 'streamBuffering'
    this.emitStop(reason)
  }

  // ─── Intent queue ─────────────────────────────────────────────────────────

  private queueIntent(intent: Intent | AppIntent) {
    // Deduplicate: same intent from two sources (e.g. player + app both emit close)
    // — second arrival is silently dropped
    if (!this.intentQueue.includes(intent)) {
      this.intentQueue.push(intent)
    }
  }

  private consumeIntent(
    candidates: readonly (Intent | AppIntent)[]
  ): (Intent & AppIntent) | null {
    for (const candidate of candidates) {
      const idx = this.intentQueue.indexOf(candidate)
      if (idx !== -1) {
        this.intentQueue.splice(idx, 1)
        return candidate as Intent & AppIntent
      }
    }
    return null
  }

  // Like consumeIntent but does NOT remove from the queue.
  // Used when the same intent needs to be read by both stop and start handlers.
  private peekIntent(
    candidates: readonly (Intent | AppIntent)[]
  ): (Intent & AppIntent) | null {
    for (const candidate of candidates) {
      if (this.intentQueue.includes(candidate)) {
        return candidate as Intent & AppIntent
      }
    }
    return null
  }

  private clearPendingSkipSegment() {
    this.pendingSkipStartReason = null
    if (
      this.forcedNextStopReason === PlayStopReason.userMoveIntro ||
      this.forcedNextStopReason === PlayStopReason.userMoveCredits
    ) {
      this.forcedNextStopReason = null
    }
  }

  // ─── Emit events ──────────────────────────────────────────────────────────

  private snapshot(): PlaybackSnapshot {
    return {
      playerId: this.state.playerId,
      currentTime: this.state.currentTime,
      contentLength: this.state.contentLength,
      httpErrorCode: this.state.httpErrorCode,
      contentErrorCode: this.state.contentErrorCode,
      audioTrackLanguage: this.state.audioTrackLanguage,
      subtitlesLanguage: this.state.subtitlesLanguage,
      videoQuality: this.state.videoQuality,
      playbackSessionId: this.state.playbackSessionId,
    }
  }

  private emitStart(reason: PlayStartReason) {
    this.trace(`emit play 🟢 start (${reason})`)
    const isFirstPlay = !this.hadStart
    this.hadStart = true
    playStart({ reason, snapshot: this.snapshot(), isFirstPlay })
    // Pre-roll: play_stop(advertisingStart) was deferred; emit now after play_start
    if (this.pendingAdStart) {
      this.pendingAdStart = false
      this.emitStop(PlayStopReason.advertisingStart)
    }
  }

  private emitStop(reason: PlayStopReason) {
    this.trace(`emit play 🔴 stop (${reason})`)
    playStop({ reason, snapshot: this.snapshot() })
  }

  private emitStartForPreStartError() {
    const intent = this.consumeIntent(
      Object.keys(INTENT_TO_START_REASON) as (Intent | AppIntent)[]
    )
    const defaultReason: PlayStartReason = this.autoplayAfterEndPending
      ? PlayStartReason.autoPlay
      : PlayStartReason.userPlay
    const reason: PlayStartReason = intent
      ? INTENT_TO_START_REASON[intent]
      : defaultReason
    this.autoplayAfterEndPending = false
    this.trace(`pre-start error; emit start with error codes (${reason})`)
    this.emitStart(reason)
  }

  // ─── Debug ────────────────────────────────────────────────────────────────

  private trace(message: string) {
    logger.trace(
      `%c${this.playerId}%c 👀 ${message}`,
      `color:${this.state.color};`,
      'color:inherit;'
    )
  }
}
